import { mdiFormatListNumbered, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import * as DS from './configuration-detail.styles';
import { ConfigurationRuleCard } from '../rules/configuration-rule-card';
import * as RS from '../rules/configuration-rule.styles';

interface IConfigurationAutomationDetailProps {
    interactionDisabled: boolean;
    store: WorkerStore;
}

export const ConfigurationAutomationDetail: React.FC<IConfigurationAutomationDetailProps> = observer(
    ({ interactionDisabled, store }) => {
        const worker = store.selectedAutomationWorker;

        if (!worker) {
            return (
                <RS.DetailPanelShell>
                    <RS.DetailPanelBody>
                        <DS.EmptyState>Select an automation worker to view configuration.</DS.EmptyState>
                    </RS.DetailPanelBody>
                </RS.DetailPanelShell>
            );
        }

        const ruleLabel = `${worker.rules.length} rule${worker.rules.length === 1 ? '' : 's'}`;

        return (
            <RS.DetailPanelShell>
                <RS.DetailPanelHeader>
                    <RS.DetailHeaderActions>
                        <div>
                            <DS.DetailHeaderRow>
                                <DS.DetailHeader>{worker.name}</DS.DetailHeader>
                                <DS.WorkerKeyChip>{worker.key}</DS.WorkerKeyChip>
                            </DS.DetailHeaderRow>
                            <DS.DetailDescription>{worker.desc}</DS.DetailDescription>
                        </div>
                        {store.readOnly ? (
                            <RS.ActiveToggle as="div" active={worker.active} aria-disabled="true">
                                <RS.ActiveDot active={worker.active} />
                                {worker.active ? 'Active' : 'Paused'}
                            </RS.ActiveToggle>
                        ) : (
                            <RS.ActiveToggle
                                type="button"
                                active={worker.active}
                                disabled={interactionDisabled}
                                onClick={() => store.toggleAutomationWorkerActive()}
                            >
                                <RS.ActiveDot active={worker.active} />
                                {worker.active ? 'Active' : 'Paused'}
                            </RS.ActiveToggle>
                        )}
                    </RS.DetailHeaderActions>
                </RS.DetailPanelHeader>

                <RS.DetailPanelBody>
                    <DS.DetailToolbar>
                        <DS.DetailHint>
                            <Icon path={mdiFormatListNumbered} size={0.7} aria-hidden={true} />
                            <span>Rules evaluate top-down by priority — the first match wins.</span>
                        </DS.DetailHint>
                        <DS.ConfiguredCount>{ruleLabel}</DS.ConfiguredCount>
                    </DS.DetailToolbar>

                    <RS.RulesScroll>
                        {worker.rules.map((rule, index) => (
                            <ConfigurationRuleCard
                                key={rule.id}
                                rule={rule}
                                index={index}
                                worker={worker}
                                store={store}
                                disabled={interactionDisabled}
                            />
                        ))}

                        {!store.readOnly && (
                            <RS.AddRuleButton
                                type="button"
                                disabled={interactionDisabled}
                                onClick={() => store.addRule()}
                            >
                                <Icon path={mdiPlus} size={0.8} aria-hidden={true} />
                                Add rule
                            </RS.AddRuleButton>
                        )}
                    </RS.RulesScroll>
                </RS.DetailPanelBody>
            </RS.DetailPanelShell>
        );
    },
);
